import {
  faFilter,
  faFilterCircleXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FuseResult } from "fuse.js";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import { DelayedFilterInput } from "../../common/DelayedFilterInput";
import { ItemsFilterModal } from "../../items/filter/ItemsFilterModal";
import { useSongsQuery } from "../../items/useSongsQuery";
import { SearchEngineEntry } from "../../search/SearchEngineEntry";
import { useSearchEngine } from "../../search/useSearchEngine";
import { ResultList } from "../../toc/ResultList";
import Sidebar from "../Sidebar";

interface Props {
  closeSidebar: () => void;
  title: ReactNode;
  /** rendered above the search field */
  actions: ReactNode;
  /** rendered below the search field */
  banner: ReactNode;
  songPicked: (songEntryId: string) => void;
  bucket: string | undefined;
}

const resultLimit = 50;

/**
 * The playlist sidebar while adding songs: the search field stays in the sticky header, the
 * results scroll below it. After picking a song, the search text is selected to search the next one.
 */
export function AddSongsSidebar({
  closeSidebar,
  title,
  actions,
  banner,
  songPicked,
  bucket,
}: Props) {
  const { t } = useTranslation();

  const { songs } = useSongsQuery();
  const searchfieldRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [selectedTonalities, setSelectedTonalities] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(false);
  //the result list reacts to up, down and enter only while typing, the carousel uses them otherwise
  const [isSearchfieldFocused, setIsSearchfieldFocused] = useState(true);

  const { search, allLanguages, allTonalities, allTags } = useSearchEngine(
    songs,
    resultLimit,
    selectedTonalities,
    selectedTags,
    selectedLanguages,
    bucket,
  );

  const [resultList, setResultList] = useState<FuseResult<SearchEngineEntry>[]>(
    [],
  );

  const isFilterActive =
    selectedTonalities.length > 0 ||
    selectedLanguages.length > 0 ||
    selectedTags.length > 0;

  function clearAllFilters() {
    setSelectedTonalities([]);
    setSelectedLanguages([]);
    setSelectedTags([]);
  }

  //new results start at the top of the scrolling sidebar
  useEffect(() => {
    const sidebar = resultsRef.current?.closest(".sidebar");
    if (sidebar) sidebar.scrollTop = 0;
  }, [searchTerm, selectedTonalities, selectedLanguages, selectedTags]);

  useEffect(() => {
    setResultList(search(searchTerm));
  }, [search, searchTerm]);

  function resultPicked(songEntryId: string) {
    songPicked(songEntryId);
    const element = searchfieldRef.current;
    if (element) {
      element.focus();
      element.setSelectionRange(0, element.value.length);
    }
  }

  const headerActions = (
    <>
      {actions}
      <div
        className="d-flex gap-2"
        onFocus={(e) =>
          setIsSearchfieldFocused(e.target === searchfieldRef.current)
        }
        onBlur={() => setIsSearchfieldFocused(false)}
      >
        <DelayedFilterInput
          inputRef={searchfieldRef}
          onSearchTermChanged={setSearchTerm}
          initialValue={searchTerm}
          autoFocus={true}
          placeholder={t("toc.searchPlaceholder")}
        />
        {isFilterActive && (
          <Button
            color="danger"
            aria-label={t("common.filter")}
            onClick={clearAllFilters}
          >
            <FontAwesomeIcon icon={faFilterCircleXmark} />
          </Button>
        )}
        <Button
          color="primary"
          aria-label={t("common.filter")}
          active={filterMenuOpen}
          onClick={() => setFilterMenuOpen((p) => !p)}
        >
          <FontAwesomeIcon icon={faFilter} />
        </Button>
      </div>
      {banner}
    </>
  );

  return (
    <Sidebar closeSidebar={closeSidebar} title={title} actions={headerActions}>
      <div ref={resultsRef}>
        <ResultList
          entrySelected={resultPicked}
          searchResults={resultList.slice(0, resultLimit)}
          keyboardNavigationEnabled={isSearchfieldFocused}
        />
      </div>

      <ItemsFilterModal
        allTonalities={allTonalities}
        selectedTonalities={selectedTonalities}
        setSelectedTonalities={setSelectedTonalities}
        allLanguages={allLanguages}
        selectedLanguages={selectedLanguages}
        setSelectedLanguages={setSelectedLanguages}
        allTags={allTags}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        isModalOpen={filterMenuOpen}
        setIsModalOpen={setFilterMenuOpen}
      />
    </Sidebar>
  );
}
